<script setup>
import { Head, Link } from '@inertiajs/vue3';
import AppLayout from '@/Layouts/AppLayout.vue';
import VariantPageWrapper from '@/Components/VariantPageWrapper.vue';
import { isBrandDirect as checkBrandDirect } from '@/lib/brand';
import LeadFunnel from '@/Components/LeadFunnel.vue';
import { CheckIcon } from '@heroicons/vue/20/solid';
import { useDesignSet } from '@/composables/useDesignSet';

const props = defineProps({
    domain: Object,
    service: String,
    branding: Object,
    designVariant: Object,
    spotImages: Object,
});

const { isClassic } = useDesignSet();

const isBrandDirect = checkBrandDirect(props.domain);
const brandCta = props.domain?.settings?.brand_cta || 'Jetzt beraten lassen';

const serviceLabel = props.service === 'wp' ? 'Wärmepumpen' : 'Photovoltaik';
const serviceSingular = props.service === 'wp' ? 'Wärmepumpe' : 'Solaranlage';
const stepSpots = ['how_step_1', 'how_step_2', 'how_step_3', 'how_step_4'];

const wpSteps = [
    { title: 'Kostenlose Beratung', desc: 'In einem ersten Gespräch klären wir Ihre Anforderungen und prüfen die Machbarkeit für Ihr Objekt. Unser Online-Fragebogen dauert nur 2 Minuten.' },
    { title: 'Anfrage weiterleiten', desc: 'Ihre Angaben gehen an regionale Fachbetriebe. Die konkrete Planung erstellt der Betrieb, den Sie später selbst wählen.' },
    { title: 'Angebotsvergleich', desc: 'Wir präsentieren Ihnen bis zu 3 Angebote von regionalen Fachbetrieben aus Ihrer Region – transparent und unverbindlich.' },
    { title: 'Fachgerechte Montage', desc: 'Ihr ausgewähltes System wird von erfahrenen Fachbetrieben termingerecht installiert, inklusive Inbetriebnahme und Einweisung.' }
];

const pvSteps = [
    { title: 'PLZ eingeben', desc: 'Geben Sie Ihre Postleitzahl ein und beantworten Sie wenige kurze Fragen zu Ihrem Dach und Ihrem Stromverbrauch. Der Online-Fragebogen dauert nur 2 Minuten.' },
    { title: 'Angebote erhalten', desc: 'Wir leiten Ihre Anfrage an regionale Fachbetriebe weiter. Sie können bis zu 3 unverbindliche Angebote erhalten - Anzahl und Rückmeldung sind nicht garantiert.' },
    { title: 'Vergleichen & entscheiden', desc: 'Vergleichen Sie die Angebote in Ruhe – Leistung, Preis, Garantien und Service. Wir helfen Ihnen, das beste Preis-Leistungs-Verhältnis zu finden.' },
    { title: 'Fachgerechte Installation', desc: 'Ihr ausgewählter Fachbetrieb installiert Ihre Solaranlage termingerecht, inklusive Anmeldung beim Netzbetreiber und Inbetriebnahme.' }
];

const steps = props.service === 'wp' ? wpSteps : pvSteps;
const heroImage = props.spotImages?.welcome_hero || null;

const qualityItems = [
    'Regionale Fachbetriebe',
    'Kostenlos und unverbindlich für Sie',
    'Vermittlungsvergütung trägt der Fachbetrieb',
];
</script>

<template>
    <Head :title="`So funktioniert es - Der Weg zu Ihrer ${serviceSingular}`">
        <meta name="description" :content="`Erfahren Sie, wie Sie in 4 einfachen Schritten zu Ihrer neuen ${serviceSingular} kommen: Beratung, Planung, Angebotsvergleich und fachgerechte Montage.`" />
        <meta property="og:title" :content="`So funktioniert es - Der Weg zu Ihrer ${serviceSingular}`" />
        <meta property="og:type" content="website" />
        <link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,600;9..144,700&family=Inter:wght@400;500;600;700&display=swap" />
    </Head>

    <AppLayout :domain="domain" :branding="branding" :designVariant="designVariant" :service="service" activePage="how" :heroMode="!isClassic" :hideChrome="!!domain?.settings?.design_variant">
        <VariantPageWrapper :domain="domain">
        <div class="smi-how">
        <!-- ===== HERO ===== -->
        <section
            class="ds-hero"
            :class="{ 'ds-hero--with-image': !isClassic && heroImage }"
            :style="!isClassic && heroImage ? { backgroundImage: `url(${heroImage})` } : {}"
            aria-label="So funktioniert es Einführung"
        >
            <div v-if="!isClassic" class="ds-hero__overlay" aria-hidden="true"></div>
            <div class="ds-hero__inner">
                <div class="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                    <span v-if="!isClassic" class="ds-hero__badge">So funktioniert es</span>
                    <h1 class="ds-hero__title">Der Weg zu Ihrer neuen {{ serviceSingular }}.</h1>
                    <p class="ds-hero__subtitle">Wir begleiten Sie Schritt für Schritt von der ersten Anfrage bis zum fertigen Angebot - einfach, transparent und ohne Stress.</p>
                </div>
            </div>
        </section>

        <!-- ===== STEPS - alternating layout ===== -->
        <section class="ds-section ds-section--gradient ds-section--pattern" aria-label="Schritte zum Ziel">
            <div class="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
                <!-- Section heading -->
                <div class="ds-steps-header">
                    <h2 class="ds-steps-header__title">In 4 Schritten zum Ziel</h2>
                    <p class="ds-steps-header__subtitle">Von der ersten Anfrage bis zur fertigen Installation - so einfach geht es.</p>
                </div>


                <div class="ds-steps">
                    <!-- Connecting line (desktop only) -->
                    <div class="ds-steps__connector" aria-hidden="true"></div>

                    <div
                        v-for="(step, idx) in steps"
                        :key="step.title"
                        class="ds-step"
                        :class="idx % 2 === 0 ? 'ds-step--normal' : 'ds-step--reverse'"
                    >
                        <!-- Image side -->
                        <div class="ds-step__media">
                            <div
                                class="ds-step__image-wrap"
                                role="img"
                                :aria-label="`Schritt ${idx + 1}: ${step.title}`"
                                :style="spotImages?.[stepSpots[idx]] ? { backgroundImage: `url(${spotImages[stepSpots[idx]]})` } : {}"
                            >
                                <div class="ds-step__image-overlay" aria-hidden="true"></div>
                                <div class="ds-step__number">{{ idx + 1 }}</div>
                            </div>
                        </div>
                        <!-- Text side -->
                        <div class="ds-step__content">
                            <span class="ds-step__label">Schritt {{ idx + 1 }} von {{ steps.length }}</span>
                            <h3 class="ds-step__title">{{ step.title }}</h3>
                            <p class="ds-step__desc">{{ step.desc }}</p>
                            <div class="ds-step__progress">
                                <div
                                    v-for="dot in steps.length"
                                    :key="dot"
                                    class="ds-step__dot"
                                    :class="{ 'ds-step__dot--active': dot <= idx + 1 }"
                                ></div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <!-- ===== QUALITY SECTION ===== -->
        <section class="ds-section ds-section--alt" aria-label="Qualitätsversprechen">
            <div class="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                <h2 class="ds-quality__title">Was das Portal leistet - und was nicht</h2>
                <p class="ds-quality__subtitle">Wir vermitteln Anfragen. Vertrag, Qualität und Ausführung liegen beim jeweiligen Fachbetrieb.</p>
                <ul class="ds-quality__list ds-quality__list--grid">
                    <li v-for="item in qualityItems" :key="item" class="ds-quality__item">
                        <div class="ds-quality__icon-wrap">
                            <CheckIcon class="ds-quality__icon" />
                        </div>
                        <span class="ds-quality__text">{{ item }}</span>
                    </li>
                </ul>
            </div>
        </section>

        <!-- ===== CTA SECTION ===== -->
        <section class="ds-cta" :class="{ 'ds-cta--immersive': !isClassic }">
            <div class="ds-cta__inner max-w-5xl mx-auto px-6 lg:px-10 text-center">
                <h2 class="ds-cta__title">Bereit für den nächsten Schritt?</h2>
                <p class="ds-cta__subtitle">Starten Sie jetzt Ihren kostenlosen Vergleich. Anzahl der Angebote ist nicht garantiert.</p>
                <div class="max-w-2xl mx-auto mt-10">
                    <div class="p-6 md:p-10" :style="{ background: 'var(--dv-card, #FFFFFF)', border: '1px solid var(--dv-border, #E8E4DB)', borderRadius: '6px', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }">
                        <LeadFunnel :domain="domain" :service="service" :design-variant="designVariant" bare :dark-mode="false" />
                    </div>
                </div>
            </div>
        </section>
        </div>
        </VariantPageWrapper>
    </AppLayout>
</template>

<style scoped>
/* ===================================================================
   HERO - driven by design-set CSS custom properties
   =================================================================== */
.ds-hero {
    position: relative;
    min-height: var(--ds-hero-min-height, 280px);
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: var(--dv-section, #f9f9f9);
    overflow: hidden;
    transition: min-height 0.3s ease;
}

.ds-hero--with-image {
    background-size: cover;
    background-position: center 40%;
    background-color: var(--dv-dark, #1a1a2e);
}

.ds-hero__overlay {
    position: absolute;
    inset: 0;
    background: var(--ds-hero-overlay-style, linear-gradient(180deg, rgba(0,0,0,0.5) 0%, rgba(0,0,0,0.7) 100%));
    z-index: 1;
}

.ds-hero__glow {
    position: absolute;
    inset: 0;
    background:
        radial-gradient(ellipse at 20% 50%, var(--dv-glow-a, rgba(137,111,218,0.25)) 0%, transparent 60%),
        radial-gradient(ellipse at 80% 30%, var(--dv-glow-b, rgba(251,144,125,0.2)) 0%, transparent 60%);
    opacity: var(--ds-hero-glow-intensity, 0.25);
    z-index: 2;
    mix-blend-mode: screen;
}

.ds-hero__inner {
    position: relative;
    z-index: 3;
    padding: 100px 0 60px;
    width: 100%;
}

.ds-hero__badge {
    display: inline-block;
    padding: 6px 20px;
    border-radius: 24px;
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 1.5px;
    text-transform: uppercase;
    color: #fff;
    background: rgba(255, 255, 255, 0.12);
    backdrop-filter: blur(12px);
    border: 1px solid rgba(255, 255, 255, 0.15);
    margin-bottom: 24px;
}

.ds-hero__title {
    font-family: 'Lexend', sans-serif;
    font-size: var(--ds-hero-title-size, clamp(28px, 4vw, 42px));
    font-weight: var(--ds-hero-title-weight, 700);
    line-height: 1.15;
    margin-bottom: 14px;
    text-shadow: var(--ds-hero-text-shadow, none);
}

.ds-hero--with-image .ds-hero__title { color: #fff; }
.ds-hero:not(.ds-hero--with-image) .ds-hero__title { color: var(--dv-text, #333); }

.ds-hero__subtitle {
    font-family: 'Source Sans 3', sans-serif;
    font-size: 17px;
    line-height: 1.7;
    max-width: 600px;
    margin: 0 auto;
}

.ds-hero--with-image .ds-hero__subtitle { color: rgba(255, 255, 255, 0.8); }
.ds-hero:not(.ds-hero--with-image) .ds-hero__subtitle { color: var(--dv-muted, #666); }

/* ===================================================================
   SECTION
   =================================================================== */
.ds-section {
    padding: 64px 0;
    background: var(--dv-page, #fff);
    position: relative;
}

@media (min-width: 768px) {
    .ds-section { padding: 80px 0; }
}

@media (min-width: 1024px) {
    .ds-section { padding: 96px 0; }
}

.ds-section--alt {
    background: var(--dv-section, #f9f9f9);
}

/* ===================================================================
   SECTION FADE-IN ANIMATION
   =================================================================== */
.section-fade-in {
    opacity: 0;
    transform: translateY(24px);
    animation: sectionReveal 0.6s ease-out forwards;
}

.section-fade-in:nth-child(2) { animation-delay: 0.1s; }
.section-fade-in:nth-child(3) { animation-delay: 0.2s; }
.section-fade-in:nth-child(4) { animation-delay: 0.3s; }
.section-fade-in:nth-child(5) { animation-delay: 0.4s; }

@keyframes sectionReveal {
    to {
        opacity: 1;
        transform: translateY(0);
    }
}

/* ===================================================================
   STEPS HEADER
   =================================================================== */
.ds-steps-header {
    text-align: center;
    margin-bottom: 48px;
}

@media (min-width: 768px) {
    .ds-steps-header { margin-bottom: 64px; }
}

.ds-steps-header__title {
    font-family: 'Lexend', sans-serif;
    font-size: var(--ds-section-title-size, clamp(24px, 3vw, 32px));
    font-weight: var(--ds-hero-title-weight, 700);
    color: var(--dv-text, #333);
    margin-bottom: 12px;
    line-height: 1.25;
}

.ds-steps-header__subtitle {
    font-family: 'Source Sans 3', sans-serif;
    font-size: 16px;
    color: var(--dv-muted, #666);
    line-height: 1.6;
    max-width: 520px;
    margin: 0 auto;
}

/* ===================================================================
   STEPS - alternating image / text rows
   =================================================================== */
.ds-steps {
    display: flex;
    flex-direction: column;
    gap: 36px;
    position: relative;
}

@media (min-width: 640px) { .ds-steps { gap: 48px; } }

@media (min-width: 1024px) {
    .ds-steps { gap: 80px; }
}

/* Connecting vertical line (desktop) */
.ds-steps__connector {
    display: none;
}

@media (min-width: 1024px) {
    .ds-steps__connector {
        display: block;
        position: absolute;
        left: 50%;
        top: 0;
        bottom: 0;
        width: 2px;
        background: linear-gradient(
            180deg,
            transparent 0%,
            color-mix(in srgb, var(--brand-accent) 30%, transparent) 8%,
            color-mix(in srgb, var(--brand-accent) 20%, transparent) 92%,
            transparent 100%
        );
        transform: translateX(-50%);
        z-index: 0;
    }
}

.ds-step {
    display: flex;
    flex-direction: column;
    gap: var(--ds-section-inner-gap, 24px);
    align-items: center;
    position: relative;
    z-index: 1;
}

@media (min-width: 1024px) {
    .ds-step { flex-direction: row; gap: 48px; }
    .ds-step--reverse { flex-direction: row-reverse; }
}

.ds-step__media {
    width: 100%;
    flex-shrink: 0;
}

@media (min-width: 1024px) {
    .ds-step__media { width: 50%; }
}

.ds-step__image-wrap {
    position: relative;
    aspect-ratio: 16 / 10;
    border-radius: var(--dv-radius-xl, 16px);
    overflow: hidden;
    background-size: cover;
    background-position: center;
    background-color: var(--dv-dark, #1a1a2e);
    box-shadow: var(--ds-card-shadow, 0 2px 8px rgba(0,0,0,0.08));
    transform: translateZ(0);
    transition:
        transform 200ms ease-out,
        box-shadow 200ms ease-out;
    transform-style: preserve-3d;
}

@media (min-width: 1024px) {
    .ds-step__image-wrap { aspect-ratio: 4 / 3; }
}

.ds-step:hover .ds-step__image-wrap {
    box-shadow: 0 4px 20px rgba(0,0,0,0.1);
}

.ds-step__image-overlay {
    position: absolute;
    inset: 0;
    background: linear-gradient(135deg, rgba(0,0,0,0.1) 0%, rgba(0,0,0,0.3) 100%);
    z-index: 1;
}

.ds-step__number {
    position: absolute;
    top: 20px;
    left: 20px;
    width: 48px;
    height: 48px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-family: 'Lexend', sans-serif;
    font-size: 18px;
    font-weight: 800;
    color: #fff;
    background: var(--brand-accent);
    border-radius: 50%;
    z-index: 2;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2), 0 0 0 4px rgba(255,255,255,0.25);
    letter-spacing: -0.5px;
}

/* Step label accent underline */
.ds-step__label {
    position: relative;
    padding-bottom: 0;
}

/* Steps header accent underline */

.ds-step__content {
    width: 100%;
    display: flex;
    flex-direction: column;
    justify-content: center;
}

@media (min-width: 1024px) {
    .ds-step__content { width: 50%; }
}

.ds-step__label {
    font-family: 'Source Sans 3', sans-serif;
    font-size: 13px;
    font-weight: 600;
    color: var(--brand-accent-fg, var(--brand-accent));
    text-transform: uppercase;
    letter-spacing: 1.2px;
    margin-bottom: 4px;
}

.ds-step__title {
    font-family: 'Lexend', sans-serif;
    font-size: var(--ds-section-title-size, 26px);
    font-weight: var(--ds-hero-title-weight, 700);
    color: var(--dv-text, #333);
    margin-top: 10px;
    margin-bottom: 14px;
    line-height: 1.25;
}

.ds-step__desc {
    font-family: 'Source Sans 3', sans-serif;
    font-size: 15px;
    color: var(--dv-muted, #666);
    line-height: 1.7;
}

/* Progress dots */
.ds-step__progress {
    display: flex;
    gap: 8px;
    margin-top: 20px;
}

.ds-step__dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: var(--dv-border, #e5e7eb);
    transition: background 200ms ease-out, transform 200ms ease-out;
}

.ds-step__dot--active {
    background: var(--brand-accent);
    transform: scale(1.15);
}

/* ===================================================================
   QUALITY SECTION
   =================================================================== */
.ds-quality {
    display: flex;
    flex-direction: column;
    gap: 40px;
    align-items: center;
}

@media (min-width: 1024px) {
    .ds-quality {
        flex-direction: row;
        gap: 48px;
    }
}

.ds-quality__content {
    flex: 1;
}

.ds-quality__title {
    font-family: 'Lexend', sans-serif;
    font-size: var(--ds-section-title-size, 32px);
    font-weight: var(--ds-hero-title-weight, 700);
    color: var(--dv-text, #333);
    margin-bottom: 12px;
    line-height: 1.25;
}

.ds-quality__subtitle {
    font-family: 'Source Sans 3', sans-serif;
    font-size: 15px;
    color: var(--dv-muted, #666);
    line-height: 1.6;
    margin-bottom: 28px;
}

.ds-quality__list {
    list-style: none;
    padding: 0;
    margin: 0;
    display: flex;
    flex-direction: column;
    gap: 16px;
}

.ds-quality__item {
    display: flex;
    align-items: center;
    gap: 14px;
}

.ds-quality__icon-wrap {
    width: 32px;
    height: 32px;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    background: var(--brand-accent-soft, rgba(251,144,125,0.1));
    border-radius: 8px;
}

.ds-quality__icon {
    width: 18px;
    height: 18px;
    flex-shrink: 0;
    color: var(--brand-accent-fg, var(--brand-accent));
}

.ds-quality__text {
    font-family: 'Source Sans 3', sans-serif;
    font-size: 15px;
    color: var(--dv-text, #444);
    line-height: 1.6;
    font-weight: 500;
}

.ds-quality__cta {
    width: 100%;
    flex-shrink: 0;
}

@media (min-width: 1024px) {
    .ds-quality__cta { width: 520px; }
}

/* ===================================================================
   CTA SECTION
   =================================================================== */
.ds-cta {
    padding: 80px 0;
    background: var(--dv-page, #fff);
    position: relative;
    overflow: hidden;
}

@media (min-width: 768px) {
    .ds-cta { padding: 100px 0; }
}

.ds-cta--immersive {
    background: var(--dv-dark, #1a1a2e);
}

.ds-cta__glow {
    position: absolute;
    inset: 0;
    background:
        radial-gradient(ellipse at 30% 50%, var(--dv-glow-a, rgba(137,111,218,0.2)) 0%, transparent 60%),
        radial-gradient(ellipse at 70% 50%, var(--dv-glow-b, rgba(251,144,125,0.15)) 0%, transparent 60%);
    opacity: var(--ds-hero-glow-intensity, 0.3);
    pointer-events: none;
    z-index: 0;
}

.ds-cta__inner {
    position: relative;
    z-index: 1;
}

.ds-cta__title {
    font-family: 'Lexend', sans-serif;
    font-size: var(--ds-section-title-size, 32px);
    font-weight: var(--ds-hero-title-weight, 700);
    margin-bottom: 12px;
    line-height: 1.25;
}

.ds-cta--immersive .ds-cta__title { color: #fff; }
.ds-cta:not(.ds-cta--immersive) .ds-cta__title { color: var(--dv-text, #333); }

.ds-cta__subtitle {
    font-family: 'Source Sans 3', sans-serif;
    font-size: 16px;
    line-height: 1.7;
    margin-bottom: 32px;
}

.ds-cta--immersive .ds-cta__subtitle { color: rgba(255, 255, 255, 0.7); }
.ds-cta:not(.ds-cta--immersive) .ds-cta__subtitle { color: var(--dv-muted, #666); }

.ds-cta__funnel {
    max-width: 480px;
    margin: 0 auto;
}

.smi-how {
    --dv-glow-a: rgba(15, 23, 42, 0.12);
    --dv-glow-b: rgba(234, 88, 12, 0.12);
    --brand-accent: #ea580c;
    background: #f8fafc;
    font-family: Inter, system-ui, sans-serif;
}
.smi-how .ds-hero__title,
.smi-how .ds-steps-header__title,
.smi-how .ds-quality__title,
.smi-how .ds-cta__title {
    font-family: Fraunces, Georgia, serif;
    font-weight: 700;
}
.smi-how .ds-hero:not(.ds-hero--with-image) { background: #0f172a; }
.smi-how .ds-hero:not(.ds-hero--with-image) .ds-hero__title,
.smi-how .ds-hero:not(.ds-hero--with-image) .ds-hero__subtitle { color: #f8fafc; }
</style>
